import React from "react";
import * as Icon from "react-feather";

import JaneReportService from "../../../../services/JaneReportService";
import UtilityService from "../../../../services/UtilityService";

import WComponent from "../../../common/WComponent";
import Loader from "../../../common/loader/Loader";

import ToolsCollapsiblePanel from "../kit/ToolsCollapsiblePanel";
import ToolsNotice from "../kit/ToolsNotice";

import "./correspondence-panel.css";

class CorrespondencePanel extends WComponent {
	constructor(props) {
		super(props);

		this.state = {
			isLoading: true,
			hasLoadedOnce: false,
			current: null,
			history: [],
			pastedText: "",
			isReading: false,
			answer: null,
			readRequest: null,
			decisionByKey: {},
			isSaving: false,
			isTextShown: false,
			isReportsChanged: false,
			isTextChanged: false,
			refused: [],
			errorMessage: ""
		};
	}

	async componentDidMount() {
		await this.load();
	}

	async componentDidUpdate(previousProps) {
		const { answer } = this.state;
		const previousSignature = this.soldItemsSignature({ soldItems: previousProps.soldItems });
		const currentSignature = this.soldItemsSignature({ soldItems: this.props.soldItems });
		const haveReportsChanged = previousSignature !== currentSignature;

		if (haveReportsChanged && answer) {
			await this.update({ answer: null, readRequest: null, decisionByKey: {}, isReportsChanged: true });
		}
	}

	soldItemsSignature = ({ soldItems }) => {
		const rows = soldItems || [];
		const itemKeys = rows.map(row => `${row.item}|${row.incomeCategory}`);
		const signature = itemKeys.join("\n");
		return signature;
	};

	load = async () => {
		const { clientId } = this.props;

		await this.update({ isLoading: true, errorMessage: "" });
		const result = await JaneReportService.readCorrespondence({ clientId });

		if (result.error) {
			await this.update({ isLoading: false, hasLoadedOnce: true, errorMessage: result.error });
			return;
		}

		await this.update({ isLoading: false, hasLoadedOnce: true, current: result.current, history: result.history || [] });
	};

	onPastedTextChanged = async ({ target }) => {
		const { answer } = this.state;

		if (answer) {
			await this.update({ pastedText: target.value, answer: null, readRequest: null, decisionByKey: {}, isTextChanged: true });
			return;
		}

		await this.update({ pastedText: target.value });
	};

	onTextToggled = async () => {
		const { isTextShown } = this.state;
		await this.update({ isTextShown: !isTextShown });
	};

	onReadClicked = async () => {
		const { clientId, soldItems } = this.props;
		const { pastedText } = this.state;
		const soldItemsRead = soldItems || [];

		await this.update({ isReading: true, isReportsChanged: false, isTextChanged: false, refused: [] });
		const result = await JaneReportService.proposeFromCorrespondence({ clientId, pastedText, soldItems: soldItemsRead });
		await this.update({ isReading: false });

		if (result.error) {
			UtilityService.alert({ type: "error", text: result.error });
			return;
		}

		await this.update({ answer: result, readRequest: { pastedText, soldItems: soldItemsRead }, decisionByKey: {} });
	};

	onDecided = async ({ proposalKey, decision }) => {
		const { decisionByKey } = this.state;
		await this.update({ decisionByKey: { ...decisionByKey, [proposalKey]: decision } });
	};

	onUndone = async ({ proposalKey }) => {
		const { decisionByKey } = this.state;
		const remainingDecisions = { ...decisionByKey };
		delete remainingDecisions[proposalKey];
		await this.update({ decisionByKey: remainingDecisions });
	};

	onAllDecided = async ({ decision }) => {
		const { answer } = this.state;
		const decisionByKey = {};
		for (const proposal of answer.proposals) {
			decisionByKey[proposal.proposalKey] = decision;
		}
		await this.update({ decisionByKey });
	};

	buildDecisions = () => {
		const { answer, decisionByKey } = this.state;

		const decisions = answer.proposals.map(proposal => ({
			kind: proposal.kind,
			target: proposal.target,
			proposedReferenceId: proposal.proposed.referenceId,
			proposedReferenceName: proposal.proposed.referenceName,
			currentReferenceIdSeen: proposal.current.referenceId || null,
			currentIsConfirmedSeen: Boolean(proposal.current.isConfirmed),
			quote: proposal.quote,
			reasoning: proposal.reasoning,
			isAccepted: decisionByKey[proposal.proposalKey] === "accepted"
		}));
		return decisions;
	};

	clearReview = async () => {
		await this.update({ answer: null, readRequest: null, decisionByKey: {} });
	};

	/**
	 * Gives each refused row the label the preparer saw on screen, because the api names a role by its internal key (fees, transit) rather than its label.
	 * @param {Object} params
	 * @param {Array} params.refused the rows the api could not save, each with kind, target and why
	 * @param {Array} params.proposals the proposals the preparer reviewed, each with proposalKey and targetLabel
	 * @return {Array} the refused rows, each with a label
	 */
	labelRefusedRows = ({ refused, proposals }) => {
		const labelledRows = refused.map(row => {
			const rowProposalKey = `${row.kind}:${row.target}`;
			const matchingProposal = proposals.find(proposal => proposal.proposalKey === rowProposalKey);
			const proposalLabel = matchingProposal ? matchingProposal.targetLabel : null;
			const label = proposalLabel || row.target;
			return { ...row, label };
		});
		return labelledRows;
	};

	onSaveClicked = async () => {
		const { clientId, onChanged } = this.props;
		const { readRequest, answer } = this.state;

		const decisions = this.buildDecisions();
		const acceptedCount = decisions.filter(decision => decision.isAccepted).length;

		if (acceptedCount === 0) {
			await this.clearReview();
			UtilityService.alert({ type: "info", text: "Nothing was saved — every proposal was rejected." });
			return;
		}

		await this.update({ isSaving: true });
		const result = await JaneReportService.saveCorrespondenceDecisions({
			clientId,
			pastedText: readRequest.pastedText,
			soldItems: readRequest.soldItems,
			decisions
		});
		await this.update({ isSaving: false });

		if (result.error) {
			UtilityService.alert({ type: "error", text: result.error });
			return;
		}

		const refusedRows = result.refused || [];
		const refused = this.labelRefusedRows({ refused: refusedRows, proposals: answer.proposals });
		const savedText = `Saved ${result.savedCount} ${result.savedCount === 1 ? "change" : "changes"} from the client's instructions`;
		const refusedText = refused.length > 0 ? ` · ${refused.length} could not be saved` : "";
		const toastType = result.savedCount > 0 ? "success" : "error";
		UtilityService.alert({ type: toastType, text: `${savedText}${refusedText}` });

		const isAnythingSaved = result.savedCount > 0;
		if (isAnythingSaved) {
			await this.update({ refused, pastedText: "", isTextShown: false });
		} else {
			await this.update({ refused });
		}
		await this.clearReview();
		await this.load();

		if (onChanged) {
			await onChanged();
		}
	};

	groupItemProposals = ({ proposals }) => {
		const groups = new Map();

		for (const proposal of proposals) {
			const category = proposal.incomeCategory || "Uncategorised";
			if (!groups.has(category)) {
				groups.set(category, { category, proposals: [] });
			}
			groups.get(category).proposals.push(proposal);
		}

		const groupList = [...groups.values()];
		return groupList;
	};

	renderBadge = () => {
		const { current } = this.state;

		if (!current) {
			return <span className="tk-badge tk-badge--neutral">None yet</span>;
		}

		const pastedOn = UtilityService.formatDate({ value: current.pastedAt });
		return <span className="tk-badge tk-badge--accent">{`From ${current.pastedByName}, ${pastedOn}`}</span>;
	};

	renderCurrent = () => {
		const { current, history, isTextShown } = this.state;

		if (!current) {
			return null;
		}

		const pastedOn = UtilityService.formatDate({ value: current.pastedAt });
		const historyCount = history.length;
		const historyText = `Replaced ${historyCount} earlier ${historyCount === 1 ? "version" : "versions"}`;

		return (
			<div className="correspondence__current">
				<div className="correspondence__current-head">
					<span className="tk-text-muted">
						In use: pasted by <span className="tk-text-strong">{current.pastedByName}</span> on {pastedOn}
					</span>
					<button className="tk-text-action tk-text-action--xs" onClick={this.onTextToggled} type="button">
						{isTextShown ? "Hide the text" : "Show the text"}
					</button>
				</div>
				{historyCount > 0 && <div className="correspondence__history tk-text-muted">{historyText}</div>}
				{isTextShown && <div className="correspondence__pasted-text">{current.pastedText}</div>}
			</div>
		);
	};

	renderPasteBox = () => {
		const { pastedText, isReading, isSaving } = this.state;
		const isBlank = pastedText.trim() === "";

		return (
			<div className="correspondence__paste">
				<label className="tk-field-label" htmlFor="correspondence-pasted-text">
					Paste the client's email or list
				</label>
				<textarea
					id="correspondence-pasted-text"
					className="tk-input tk-input--multiline correspondence__textarea"
					value={pastedText}
					onChange={this.onPastedTextChanged}
					disabled={isReading || isSaving}
				/>
				<div className="tk-note">Paste only the part about where things go in QuickBooks — no signatures or patient details. Nothing changes until you save.</div>
				<div className="tk-action-row">
					<button className="tk-button tk-button--primary tk-button--compact" onClick={this.onReadClicked} type="button" disabled={isBlank || isReading || isSaving}>
						{isReading ? "Reading…" : "Read these instructions"}
					</button>
				</div>
			</div>
		);
	};

	renderChange = ({ proposal }) => {
		if (proposal.isConfirmingUnconfirmed) {
			return (
				<div className="correspondence__change">
					Client agrees with the suggested <span className="tk-text-strong">{proposal.proposed.referenceName}</span> — accept to confirm it
				</div>
			);
		}

		const currentName = proposal.current.referenceId ? proposal.current.referenceName : "Not set";

		return (
			<div className="correspondence__change">
				<span className="tk-text-muted">Now:</span>
				<span className="correspondence__current-name">{currentName}</span>
				<Icon.ArrowRight size={13} className="correspondence__arrow" />
				<span className="tk-text-muted">Client says:</span>
				<span className="tk-text-strong">{proposal.proposed.referenceName}</span>
			</div>
		);
	};

	renderDecision = ({ proposal }) => {
		const { decisionByKey, isSaving } = this.state;
		const { proposalKey } = proposal;
		const decision = decisionByKey[proposalKey];

		if (decision) {
			const isAccepted = decision === "accepted";
			return (
				<div className="correspondence__decision">
					<span className={isAccepted ? "tk-badge tk-badge--success" : "tk-badge tk-badge--neutral"}>{isAccepted ? "Accepted" : "Rejected"}</span>
					<button className="tk-text-action tk-text-action--muted tk-text-action--xs" onClick={() => this.onUndone({ proposalKey })} type="button" disabled={isSaving}>
						Undo
					</button>
				</div>
			);
		}

		return (
			<div className="correspondence__decision">
				<button className="tk-button tk-button--secondary tk-button--compact" onClick={() => this.onDecided({ proposalKey, decision: "accepted" })} type="button" disabled={isSaving}>
					Accept
				</button>
				<button className="tk-text-action tk-text-action--muted" onClick={() => this.onDecided({ proposalKey, decision: "rejected" })} type="button" disabled={isSaving}>
					Reject
				</button>
			</div>
		);
	};

	renderConflictBadge = ({ proposal }) => {
		if (!proposal.isConflictWithConfirmed) {
			return null;
		}

		const chosenByName = proposal.current.chosenByName;
		const conflictText = chosenByName ? `Conflicts with ${chosenByName}'s choice` : "Conflicts with a confirmed choice";
		return <span className="tk-badge tk-badge--warning">{conflictText}</span>;
	};

	renderProposal = ({ proposal }) => {
		const hasAmount = proposal.netAmount !== null && proposal.netAmount !== undefined;

		return (
			<div className="tk-list__row tk-list__row--top correspondence__proposal" key={proposal.proposalKey}>
				<div className="tk-list__main">
					<div className="correspondence__target">
						<span className="tk-list__title">{proposal.targetLabel}</span>
						{hasAmount && <span className="correspondence__amount tk-text-muted">{UtilityService.formatMoney({ value: proposal.netAmount })}</span>}
						{this.renderConflictBadge({ proposal })}
					</div>
					{this.renderChange({ proposal })}
					<blockquote className="correspondence__quote">{proposal.quote}</blockquote>
					{proposal.reasoning && <div className="tk-list__subtitle">{proposal.reasoning}</div>}
				</div>
				{this.renderDecision({ proposal })}
			</div>
		);
	};

	renderItemGroup = ({ group }) => {
		return (
			<div className="correspondence__group" key={group.category}>
				<div className="correspondence__category">{group.category}</div>
				<div className="tk-list tk-list--flush">{group.proposals.map(proposal => this.renderProposal({ proposal }))}</div>
			</div>
		);
	};

	renderReviewList = () => {
		const { answer } = this.state;

		const roleProposals = answer.proposals.filter(proposal => proposal.kind === "accountRole");
		const itemProposals = answer.proposals.filter(proposal => proposal.kind === "itemHead");
		const itemGroups = this.groupItemProposals({ proposals: itemProposals });

		return (
			<div className="correspondence__review">
				{roleProposals.length > 0 && (
					<div className="correspondence__section">
						<div className="tk-section-label">Journal roles</div>
						<div className="tk-list tk-list--flush">{roleProposals.map(proposal => this.renderProposal({ proposal }))}</div>
					</div>
				)}
				{itemGroups.length > 0 && (
					<div className="correspondence__section">
						<div className="tk-section-label">Item revenue accounts</div>
						{itemGroups.map(group => this.renderItemGroup({ group }))}
					</div>
				)}
			</div>
		);
	};

	renderUnplaced = () => {
		const { answer } = this.state;
		const unplaced = answer.unplaced || [];

		if (unplaced.length === 0) {
			return null;
		}

		return (
			<div className="correspondence__section">
				<div className="tk-section-label">Not placed on a mapping</div>
				<div className="tk-list tk-list--flush">
					{unplaced.map((passage, index) => (
						<div className="tk-list__row tk-list__row--top" key={`${index}-${passage.quote}`}>
							<div className="tk-list__main">
								<blockquote className="correspondence__quote">{passage.quote}</blockquote>
								<div className="tk-list__subtitle">{passage.why}</div>
							</div>
						</div>
					))}
				</div>
			</div>
		);
	};

	renderCoverage = () => {
		const { answer } = this.state;
		const { coverage, alreadyMatchingCount, droppedCount, isItemSplitOn } = answer;

		const itemsPart = isItemSplitOn ? `${coverage.itemsNamed} of ${coverage.itemsAsked} items and ` : "";
		const coverageText = `Named ${itemsPart}${coverage.rolesNamed} of ${coverage.rolesAsked} journal roles`;
		const matchingText = alreadyMatchingCount > 0 ? ` · ${alreadyMatchingCount} already match` : "";
		const droppedText = `${droppedCount} ${droppedCount === 1 ? "answer was" : "answers were"} set aside because their passage is not in the text or their account is not in this client's chart.`;

		return (
			<div className="correspondence__coverage">
				<div className="tk-note">{`${coverageText}${matchingText}`}</div>
				{!isItemSplitOn && <div className="tk-note">Item accounts were not read — this client's journal does not split revenue by item.</div>}
				{droppedCount > 0 && <div className="tk-note">{droppedText}</div>}
			</div>
		);
	};

	renderFooter = () => {
		const { answer, decisionByKey, isSaving } = this.state;

		const proposalCount = answer.proposals.length;
		const decidedValues = Object.values(decisionByKey);
		const undecidedCount = proposalCount - decidedValues.length;
		const acceptedCount = decidedValues.filter(decision => decision === "accepted").length;

		let saveLabel = `Save ${acceptedCount} accepted ${acceptedCount === 1 ? "change" : "changes"}`;
		if (undecidedCount > 0) {
			saveLabel = `Decide ${undecidedCount} more`;
		} else if (acceptedCount === 0) {
			saveLabel = "Save — nothing accepted";
		}
		if (isSaving) {
			saveLabel = "Saving…";
		}

		return (
			<div className="correspondence__footer">
				<div className="correspondence__bulk">
					<button className="tk-text-action tk-text-action--muted" onClick={() => this.onAllDecided({ decision: "accepted" })} type="button" disabled={isSaving}>
						Accept all
					</button>
					<button className="tk-text-action tk-text-action--muted" onClick={() => this.onAllDecided({ decision: "rejected" })} type="button" disabled={isSaving}>
						Reject all
					</button>
				</div>
				<button className="tk-button tk-button--primary tk-button--compact" onClick={this.onSaveClicked} type="button" disabled={undecidedCount > 0 || isSaving}>
					{saveLabel}
				</button>
			</div>
		);
	};

	renderAnswer = () => {
		const { answer } = this.state;

		if (!answer) {
			return null;
		}

		const hasProposals = answer.proposals.length > 0;
		const hasUnplaced = (answer.unplaced || []).length > 0;
		const isItemsReasonShown = answer.isItemSplitOn && Boolean(answer.itemsReason);

		return (
			<div className="correspondence__answer">
				{isItemsReasonShown && <ToolsNotice className="correspondence__notice" icon={<Icon.Info size={16} />} title={answer.itemsReason} />}
				{!hasProposals && !hasUnplaced && <ToolsNotice className="correspondence__notice" icon={<Icon.Info size={16} />} title="Nothing in this text changes a mapping." />}
				{hasProposals && this.renderReviewList()}
				{this.renderUnplaced()}
				{this.renderCoverage()}
				{hasProposals && this.renderFooter()}
			</div>
		);
	};

	renderRefused = () => {
		const { refused } = this.state;

		if (refused.length === 0) {
			return null;
		}

		const refusedLines = refused.map(row => <div key={`${row.kind}-${row.target}`}>{`${row.label}: ${row.why}`}</div>);
		return <ToolsNotice className="correspondence__notice" tone="warning" icon={<Icon.AlertTriangle size={16} />} title={`${refused.length} could not be saved`} detail={refusedLines} />;
	};

	render() {
		const { isLoading, hasLoadedOnce, isReportsChanged, isTextChanged, errorMessage } = this.state;
		const isFirstLoad = isLoading && !hasLoadedOnce;
		const isReloading = isLoading && hasLoadedOnce;

		if (isFirstLoad) {
			return (
				<div className="tk-panel tk-panel-loading">
					<Loader />
					<span className="tk-text-muted">Reading this client's mapping instructions…</span>
				</div>
			);
		}

		return (
			<ToolsCollapsiblePanel title="Client's mapping instructions" status={this.renderBadge()} hint="Paste a client's email to propose accounts">
				<div className="tk-lede">
					Paste what the client said about where things go in QuickBooks. Each account it would change is shown against today's mapping with the
					passage it came from, and you accept or reject each one before anything is saved.
				</div>

				{isReloading && (
					<div className="correspondence__inline-loading tk-text-muted">
						<Icon.RefreshCw size={13} />
						<span>Refreshing the instructions in use…</span>
					</div>
				)}
				{this.renderCurrent()}
				{this.renderRefused()}
				{this.renderPasteBox()}
				{isReportsChanged && <ToolsNotice className="correspondence__notice" tone="warning" icon={<Icon.AlertTriangle size={16} />} title="The reports changed — read the instructions again." />}
				{isTextChanged && <ToolsNotice className="correspondence__notice" tone="warning" icon={<Icon.AlertTriangle size={16} />} title="The text changed — read it again." />}
				{this.renderAnswer()}

				{errorMessage && <div className="tk-note">{errorMessage}</div>}
			</ToolsCollapsiblePanel>
		);
	}
}

export default CorrespondencePanel;
